import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  TableRowActionsMenu,
  ConfirmDialog,
  Typography,
  useConfirmDialog,
  snackbar,
} from '@occc-app/design-system';
import { useDeleteSegment } from '@occc/feat-segments';

import { routeCreator } from '../utils';

type SegmentActionsContainerProps = {
  id: string;
  name: string;
  isUserPermittedToEditSegment: boolean;
  isUserPermittedToDeleteSegment: boolean;
};

export const SegmentActionsContainer = ({
  id,
  name,
  isUserPermittedToEditSegment,
  isUserPermittedToDeleteSegment,
}: SegmentActionsContainerProps) => {
  const navigate = useNavigate();

  const onDeleteCompleted = () => snackbar.success('Segment has been successfully deleted.');

  const [segmentDelete] = useDeleteSegment({
    onCompleted: onDeleteCompleted,
    refetchQueries: ['segments'],
  });
  const onDelete = () => segmentDelete({ variables: { id } });

  const { open, setOpen, onCloseButtonClick, onConfirmButtonClick } = useConfirmDialog({
    onConfirm: onDelete,
    shouldStopEventPropagation: true,
  });

  const menuItems = useMemo(() => {
    const items = [];

    if (isUserPermittedToEditSegment) {
      items.push({
        label: 'Edit Segment',
        onClick: () => navigate(routeCreator.details(id)),
        dataTestId: 'edit',
      });
    }

    if (isUserPermittedToDeleteSegment) {
      items.push({
        label: 'Delete Segment',
        onClick: () => setOpen(true),
        dataTestId: 'delete',
      });
    }

    return items;
  }, [id, navigate, setOpen, isUserPermittedToEditSegment, isUserPermittedToDeleteSegment]);

  return (
    <>
      <TableRowActionsMenu menuItems={menuItems} />
      <ConfirmDialog
        dialogTitle='Are you sure?'
        open={open}
        onClose={onCloseButtonClick}
        confirmButtonText='Confirm'
        confirmButtonProps={{
          onClick: onConfirmButtonClick,
          color: 'error',
        }}
      >
        <Typography>This action will delete the Segment "{name}".</Typography>
      </ConfirmDialog>
    </>
  );
};
